import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { INotification, NotificationService } from '@services/notification.service';
import { NotificationStateService } from '@services/notification-state.service';
import { PageTitleService } from '@services/page-title.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-notification-list',
  standalone: false,
  templateUrl: './notification-list.component.html',
  styleUrls: ['./notification-list.component.scss']
})
export class NotificationListComponent implements OnInit, OnDestroy {
  notifications: INotification[] = [];
  loading: boolean = false;
  totalRecords: number = 0;
  currentPage: number = 1;
  pageSize: number = 20;

  private destroy$ = new Subject<void>();

  constructor(
    private notificationService: NotificationService,
    private notificationStateService: NotificationStateService,
    private pageTitleService: PageTitleService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    // Set page title
    this.pageTitleService.setCustomTitle(this.translateService.instant('notifications.title'));

    // Fetch initial list
    this.fetchNotifications();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }


  /**
   * Fetch notifications from backend
   */
  fetchNotifications(): void {
    this.loading = true;

    this.notificationService
      .fetchList({
        page_no: this.currentPage,
        page_size: this.pageSize
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.notifications = result.list;
          this.totalRecords = result.total;
          this.loading = false;
        },
        error: (error) => {
          console.error('[NotificationList] Error fetching notifications:', error);
          this.loading = false;
        }
      });
  }

  /**
   * Handle page change
   */
  onPageChange(event: any): void {
    this.currentPage = event.page + 1;
    this.pageSize = event.rows;
    this.fetchNotifications();
  }

  /**
   * Handle notification click
   * Note: Navigation is now handled internally by notification-page-item component
   */

  /**
   * Mark all notifications as read
   */
  markAllAsRead(): void {
    this.notificationStateService.markAllAsRead();

    // Refresh list after a short delay
    setTimeout(() => {
      this.fetchNotifications();
    }, 500);
  }

  /**
   * Get skeleton array for loading state
   */
  get skeletonArray(): number[] {
    return Array(5).fill(0);
  }

  /**
   * Check if there are any unread notifications
   */
  get hasUnreadNotifications(): boolean {
    return this.notifications.some(n => !n.isRead);
  }
}
